import { useRef, useState } from "react";
import { api, apiErr } from "@/api";
import Overlay from "@/components/Overlay";
import ImageCropper from "@/components/ImageCropper";
import IntakeItem from "@/components/IntakeItem";
import { cropSquare } from "@/lib/imageUtils";
import { useTaxonomy } from "@/hooks/useTaxonomy";
import { toast } from "sonner";
import { ImagePlus, X, Sparkles, Play } from "lucide-react";

const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];
const uid = () => Math.random().toString(36).slice(2, 9);

export default function PhotoIntake({ credits, setCredits, costs, attachTo = null, onApproved, onClose }) {
  const tax = useTaxonomy();
  const [items, setItems] = useState([]);
  const [enhanceAll, setEnhanceAll] = useState(false);
  const [cropKey, setCropKey] = useState(null);
  const batchId = useRef(uid() + Date.now().toString(36));
  const fileRef = useRef();
  const ref = useRef(items);
  ref.current = items;

  const update = (key, patch) => setItems((s) => s.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const get = (key) => ref.current.find((i) => i.key === key);
  const creditsFrom = (d) => { if (d?.ai_credits_remaining != null) { setCredits?.(d.ai_credits_remaining); } };

  const addFiles = async (files) => {
    const list = Array.from(files || []).slice(0, 20);
    if (fileRef.current) { fileRef.current.value = ""; }
    for (const file of list) {
      if (!OK_TYPES.includes(file.type)) { toast.error(`${file.name}: use JPG, PNG or WEBP`); continue; }
      const key = uid();
      const src = URL.createObjectURL(file);
      const blob = await cropSquare(src);
      setItems((s) => [...s, { key, file, src, blob, preview: URL.createObjectURL(blob), state: "ready", enhance: enhanceAll, intake: null, form: null, confirmed: [], useEnhanced: false, error: "" }]);
    }
  };

  const toggleAll = (v) => { setEnhanceAll(v); setItems((s) => s.map((i) => (i.state === "ready" ? { ...i, enhance: v } : i))); };

  const process = async (key) => {
    const item = get(key);
    let intake = item.intake;
    try {
      if (!intake) {
        update(key, { state: "checking", error: "" });
        const fd = new FormData();
        fd.append("file", item.blob, "photo.jpg");
        fd.append("original", item.file);
        fd.append("batch_id", batchId.current);
        intake = (await api.post("/exhibitor/intake", fd)).data;
        update(key, { intake });
      }
      if (item.enhance && !intake.enhanced) {
        update(key, { state: "enhancing", error: "" });
        const { data } = await api.post(`/exhibitor/intake/${intake.id}/enhance`);
        creditsFrom(data);
        intake = { ...intake, enhanced: data.enhanced };
        update(key, { intake, useEnhanced: true });
      }
      if (attachTo) { update(key, { state: "review" }); return; }
      update(key, { state: "suggesting", error: "" });
      const { data } = await api.post(`/exhibitor/intake/${intake.id}/suggest`);
      creditsFrom(data);
      update(key, { state: "review", form: { ...data.suggestion, ai: true }, confirmed: [] });
    } catch (err) {
      const msg = apiErr(err);
      update(key, { state: msg.startsWith("Rejected") ? "rejected" : "error", error: msg });
    }
  };

  const processAll = async () => {
    const queue = ref.current.filter((i) => i.state === "ready" || i.state === "error").map((i) => i.key);
    const worker = async () => { while (queue.length) { await process(queue.shift()); } };
    await Promise.all([worker(), worker(), worker()]);
  };

  const approve = async (key) => {
    const item = get(key);
    update(key, { state: "approving" });
    try {
      const { ai, needs_confirmation: _n, ...form } = item.form || {};
      const { data } = await api.post(`/exhibitor/intake/${item.intake.id}/approve`, { ...form, use_enhanced: item.useEnhanced, confirmed: item.confirmed, attach_to: attachTo });
      update(key, { state: "approved" });
      onApproved?.(data);
    } catch (err) { update(key, { state: "review" }); toast.error(apiErr(err)); }
  };

  const remove = (key) => {
    const item = get(key);
    if (item.intake) { api.delete(`/exhibitor/intake/${item.intake.id}`).catch(() => {}); }
    setItems((s) => s.filter((i) => i.key !== key));
  };

  const onCropped = async (blob) => {
    update(cropKey, { blob, preview: URL.createObjectURL(blob) });
    setCropKey(null);
  };

  const readyCount = items.filter((i) => i.state === "ready" || i.state === "error").length;
  const cropItem = items.find((i) => i.key === cropKey);

  return (
    <Overlay onClose={onClose} z="z-[60]" className="max-w-2xl" testid="photo-intake-modal">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl gold-text">{attachTo ? "Add photos to product" : "Add products from photos"}</h3>
        <button onClick={onClose} className="text-slate-400" data-testid="photo-intake-close"><X size={20} /></button>
      </div>
      <p className="text-xs text-slate-400 mt-1">
        Each photo is checked (free) for format, quality and that it shows jewellery. {attachTo ? "" : `AI then suggests details (${1} credit per product, max ${costs.catalogue_ai} per batch). `}
        Optional AI enhance: {costs.image_enhance} credit per photo. Originals are always kept. Nothing is saved until you approve. <span className="text-amber-300" data-testid="intake-credits">{credits} credits</span>
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button onClick={() => fileRef.current?.click()} className="glass px-3 py-2 rounded-xl text-sm text-slate-200 border border-amber-500/20 flex items-center gap-1.5" data-testid="intake-add-photos"><ImagePlus size={15} className="text-amber-400" /> Choose photos</button>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 glass px-3 py-2 rounded-xl border border-amber-500/20">
          <input type="checkbox" checked={enhanceAll} onChange={(e) => toggleAll(e.target.checked)} className="accent-[#C6A15B]" data-testid="intake-enhance-all" /> <Sparkles size={13} className="text-amber-400" /> Enhance all
        </label>
        {readyCount > 0 && <button onClick={processAll} className="btn-gold px-3 py-2 rounded-xl text-sm flex items-center gap-1.5 ml-auto" data-testid="intake-process-all"><Play size={14} /> Process {readyCount}</button>}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="intake-file-input" />
      {items.length === 0 ? (
        <button onClick={() => fileRef.current?.click()} className="w-full mt-4 py-12 rounded-2xl border border-dashed border-amber-500/40 text-sm text-slate-400" data-testid="intake-dropzone">Tap to choose jewellery photos (up to 20)</button>
      ) : (
        <div className="space-y-2 mt-4">
          {items.map((it) => (
            <IntakeItem key={it.key} item={it} tax={tax} attach={!!attachTo} update={(p) => update(it.key, p)}
              onCrop={() => setCropKey(it.key)} onRemove={() => remove(it.key)} onProcess={() => process(it.key)} onApprove={() => approve(it.key)} />
          ))}
        </div>
      )}
      {items.length > 0 && <button onClick={onClose} className="w-full glass py-2.5 rounded-xl mt-4 text-sm text-slate-200 border border-amber-500/20" data-testid="photo-intake-done">Done</button>}
      {cropItem && <ImageCropper src={cropItem.src} onCancel={() => setCropKey(null)} onConfirm={onCropped} />}
    </Overlay>
  );
}
